/* eslint-disable @stencil-community/own-props-must-be-private */
// Stencil
import {
  Component,
  Element,
  Host,
  Listen,
  Method,
  Prop,
  State,
  h
} from "@stencil/core";
// Other Libraries
import { MercuryBundles, getIconPath } from "@genexus/mercury";
// Custom Imports
import { config } from "../../common/config";
import { Locale } from "../../common/locale";
import { ContextMenuInfo, EntityData, GxOption } from "../../common/types";
import { mapOptionsToComboBoxItemModel } from "./helpers";
import { ChEditCustomEvent } from "@genexus/chameleon-controls-library";
import HTMLChTabularGridRowsetElement from "@genexus/chameleon-controls-library/dist/types/components/tabular-grid/rowset/tabular-grid-rowset";
import { GxIdeEntitySelectorCustomEvent } from "../../components";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/edit",
  "components/combo-box",
  "components/tabular-grid",
  "components/icon",
  "utils/form",
  "utils/layout",
  "utils/spacing",
  "chameleon/scrollbar"
];

const FILTER_ICON = getIconPath({
  category: "window-tools",
  name: "filter",
  colorType: "on-surface"
});

@Component({
  tag: "gx-ide-ww-attributes",
  styleUrl: "ww-attributes.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/ww-attributes"]
})
export class GxIdeWWAttributes {
  /**
   * Component hard-coded strings translations.
   */
  #_componentLocale: any;

  @Element() el: HTMLGxIdeWwAttributesElement;

  /* References needed to collect data */
  #chGridEl!: HTMLChTabularGridElement;

  /**
   * Attributes rendered in the table
   */
  @State() attributes: AttributeData[];

  /**
   * Selected attributes in the table of attributes
   */
  @State() selectedAttributesIds: string[] = [];

  /**
   * Show or hide advanced filters
   */
  @State() filterMore = false;

  /**
   * The attribute filter name
   */
  @State() name: string = "";

  /**
   * True if loadCallback hasn't been resolved yet
   */
  @State() loading: boolean = true;

  /**
   * The attribute object value
   */
  @State() object: EntityData;

  /**
   * The attribute filter type value
   */
  @State() type: string;

  /**
   * Callback invoked when user right-clicks on the grid
   */
  @Prop() readonly contextMenuCallback!: ContextMenuCallback;

  /**
   * Callback invoked when user deletes an object
   */
  @Prop() readonly deleteSelectionCallback!: DeleteSelectionCallback;

  /**
   * Callback invoked when user filters the objects
   */
  @Prop() readonly loadCallback!: LoadCallback;

  /**
   * Callback invoked when user executes filter action
   */
  @Prop() readonly objectActionCallback!: ObjectActionCallback;

  /**
   * Objects rendered in the object selector
   */
  @Prop() readonly objects!: GxOption[];

  /**
   * Callback invoked when user opens the selection dialog
   */
  @Prop() readonly openSelectionCallback!: OpenSelectionCallback;

  /**
   * Callback invoked when user selects or deselects an object
   */
  @Prop() readonly selectionChangeCallback!: SelectionChangeCallback;

  /**
   * Types rendered in the filter type selector
   */
  @Prop() readonly types!: GxOption[];

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
  }

  componentDidLoad() {
    this.#listenChanges();
    this.type = this.types[0].id; // before #getAttributes
    this.#getAttributes();
  }

  @Listen("keydown", { passive: true })
  keyDownHandler(eventInfo: KeyboardEvent) {
    if (document.activeElement === this.el) {
      switch (eventInfo.key) {
        case "Enter":
          this.#openSelectionCallbackHandle();
          break;
        case "Delete":
          this.#deleteSelectionCallbackHandle();
          break;
      }
    }
  }

  /**
   * Reloads the view, refreshing the filters and attributes table.
   */
  @Method()
  async reload(): Promise<void> {
    this.name = "";
    this.type = this.types[0].id;
    this.object = null;
    this.#getAttributes();
  }

  /**
   * Validate necessary data input
   */
  @Method()
  async validate(): Promise<boolean> {
    const isValid = true;
    return isValid;
  }

  #contextMenuCallbackHandler = (ev: MouseEvent): void => {
    this.contextMenuCallback({
      selection: this.selectedAttributesIds,
      clientX: ev.clientX,
      clientY: ev.clientY
    });
  };

  #deleteSelectionCallbackHandle = (): void => {
    this.deleteSelectionCallback(this.selectedAttributesIds);
  };

  #deselectAll = (): void => {
    (this.#chGridEl as any).selectAllRows(false);
  };

  #getAttributes = (): void => {
    this.loading = true;
    const filters: AttributeFiltersData = {
      name: this.name.trim(),
      type: this.type,
      object: this.object?.id
    };
    this.loadCallback(filters).then((items: AttributeData[]) => {
      this.attributes = items;
      if (this.selectedAttributesIds?.length > 0) {
        this.selectedAttributesIds = [];
        this.#deselectAll();
      }
      this.loading = false;
    });
  };

  #listenChanges = (): void => {
    // for grid selection
    this.#chGridEl.addEventListener("selectionChanged", (ev: any): void => {
      this.selectedAttributesIds = ev.detail.rowsId;
      this.selectionChangeCallback(this.selectedAttributesIds);
    });
    this.#chGridEl.addEventListener("contextmenu", (ev: MouseEvent): void => {
      ev.preventDefault();
      ev.stopPropagation();
      this.#contextMenuCallbackHandler(ev);
    });

    this.#chGridEl.addEventListener("rowDoubleClicked", (): void => {
      this.#openSelectionCallbackHandle();
    });
  };

  #openSelectionCallbackHandle = (): void => {
    this.openSelectionCallback(this.selectedAttributesIds);
  };

  #entityCallbackHandler = async (): Promise<null> => {
    const result = await this.objectActionCallback();
    this.object = {
      id: result.id,
      name: result.name,
      iconSrc: result.iconSrc
    };
    return new Promise(resolve => {
      resolve(null);
    });
  };

  #entityValueChangedHandler = (
    event: GxIdeEntitySelectorCustomEvent<EntityData>
  ) => {
    this.object = event.detail;
    this.#getAttributes();
  };

  #renderAttributesGrid = (): Element => {
    const tabularGridEmpty = this.attributes?.length === 0 || !this.attributes;
    return (
      <ch-tabular-grid
        class={{
          "tabular-grid": true,
          "tabular-grid--empty": tabularGridEmpty,
          "main": true
        }}
        ref={(el: HTMLChTabularGridElement) => (this.#chGridEl = el)}
        row-selection-mode="multiple"
        part="ch-grid-attributes"
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            column-name=""
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#_componentLocale.tableHead.name}
            column-name-position="text"
            size={config.tabularGrid.colSize.auto}
            class="tabular-grid-column"
            settingable={false}
            sortable={false}
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#_componentLocale.tableHead.type}
            column-name-position="text"
            class="tabular-grid-column"
            size={config.tabularGrid.colSize.auto}
            settingable={false}
            sortable={false}
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#_componentLocale.tableHead.description}
            column-name-position="text"
            size={config.tabularGrid.colSize.auto}
            class="tabular-grid-column"
            settingable={false}
            sortable={false}
          ></ch-tabular-grid-column>
        </ch-tabular-grid-columnset>

        {this.#evaluateAttributesGridContent()}
      </ch-tabular-grid>
    );
  };

  #evaluateAttributesGridContent = (): HTMLChTabularGridRowsetElement => {
    if (this.loading) {
      return (
        <ch-tabular-grid-rowset class="tabular-grid-rowset">
          <ch-tabular-grid-rowset-empty>
            <gx-ide-loader
              loaderTitle={this.#_componentLocale.loader.title}
              description={this.#_componentLocale.loader.description}
              show
            ></gx-ide-loader>
          </ch-tabular-grid-rowset-empty>
        </ch-tabular-grid-rowset>
      );
    } else if (this.attributes.length) {
      return (
        <ch-tabular-grid-rowset class="tabular-grid-rowset">
          {this.attributes.map((attr: AttributeData) => (
            <ch-tabular-grid-row class="tabular-grid-row" rowid={attr.id}>
              <ch-tabular-grid-cell class="tabular-grid-cell">
                <ch-image class="icon-xs" src={attr.icon}></ch-image>
              </ch-tabular-grid-cell>
              <ch-tabular-grid-cell class="tabular-grid-cell">
                {attr.name}
              </ch-tabular-grid-cell>
              <ch-tabular-grid-cell class="tabular-grid-cell">
                {attr.type}
              </ch-tabular-grid-cell>
              <ch-tabular-grid-cell class="tabular-grid-cell">
                {attr.description}
              </ch-tabular-grid-cell>
            </ch-tabular-grid-row>
          ))}
        </ch-tabular-grid-rowset>
      );
    } else {
      // filter returned none
      return (
        <ch-tabular-grid-rowset class="tabular-grid-rowset">
          <ch-tabular-grid-rowset-empty>
            <gx-ide-empty-state
              stateIconSrc={FILTER_ICON}
              stateTitle={this.#_componentLocale.emptyStateTitle}
              isAnimated={true}
            ></gx-ide-empty-state>
          </ch-tabular-grid-rowset-empty>
        </ch-tabular-grid-rowset>
      );
    }
  };

  #renderFilter = (): Element => {
    return (
      <header class="header field-group dialog-header-with-border spacing-body">
        <div class="field field-inline name__field">
          <label class="label name__label" htmlFor="name">
            {this.#_componentLocale.filter.name}
          </label>
          <ch-edit
            // Name
            id="name"
            class="input"
            value={this.name}
            debounce={300}
            onInput={this.#nameInputHandler}
            part="filter-name"
          ></ch-edit>
        </div>

        <div class="field field-inline type__field">
          <label class="label" htmlFor="type">
            {this.#_componentLocale.filter.type}
          </label>
          <ch-combo-box-render
            // Type
            id="type"
            class="combo-box"
            disabled={!this.types}
            value={this.type}
            model={mapOptionsToComboBoxItemModel(this.types ?? [])}
            part="filter-type"
            onInput={this.#typeInputHandler}
          ></ch-combo-box-render>
        </div>

        {/* <button
          // TODO: Check with Design if it maes sense to have a button that toggles only one control.
          // More
          class="button-primary more__btn"
          onClick={this.#showMoreFilter}
          part="button button--more-less"
        >
          {this.filterMore
            ? this.#_componentLocale.filter.btnLess
            : this.#_componentLocale.filter.btnMore}
        </button> */}

        <div class="field field-inline object-selector__field">
          <label class="label" htmlFor="object-selector">
            {this.#_componentLocale.filter.object}
          </label>
          <gx-ide-entity-selector
            // Object
            id="object-selector"
            value={this.object}
            labelPosition="none"
            defaultValue={null}
            selectEntityCallback={this.#entityCallbackHandler}
            onValueChanged={this.#entityValueChangedHandler}
            part="filter-module-folder"
          ></gx-ide-entity-selector>
        </div>
      </header>
    );
  };

  #selectAll = (): void => {
    (this.#chGridEl as any).selectAllRows();
  };

  #nameInputHandler = (e: ChEditCustomEvent<string> | InputEvent) => {
    this.name = (e as ChEditCustomEvent<string>).detail.trim();
    this.#getAttributes();
  };

  #typeInputHandler = (e: CustomEvent<string> | InputEvent) => {
    this.type = e.detail as string;
    this.#getAttributes();
  };

  render(): void {
    const btnDisabled = !this.attributes || this.attributes.length === 0;
    const matchingAttributesLabel =
      this.attributes?.length === 1
        ? this.#_componentLocale.matchingAttribute
        : this.#_componentLocale.matchingAttributes;
    const attributesLength = this.attributes?.length || 0;

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          {this.#renderFilter()}

          {this.#renderAttributesGrid()}

          <footer class="dialog-footer dialog-footer-with-border spacing-body-block-end spacing-body-inline dialog-footer-space-between">
            <p class="body-regular-s matching-attributes">
              {`${attributesLength} ${matchingAttributesLabel}`}
            </p>

            <button
              id="button-select-all"
              class="button-primary"
              onClick={this.#selectAll}
              disabled={btnDisabled}
              part="button button--select-all"
            >
              {this.#_componentLocale.footer.btnSelectAll}
            </button>
          </footer>
        </section>
      </Host>
    );
  }
}

export type ContextMenuCallback = (
  contextMenuInfo: ContextMenuInfo
) => Promise<void>;

export type DeleteSelectionCallback = (ids: string[]) => Promise<void>;

export type LoadCallback = (
  filters: AttributeFiltersData
) => Promise<AttributeData[]>;

export type ObjectActionCallback = () => Promise<EntityData | undefined>;

export type OpenSelectionCallback = (ids: string[]) => Promise<void>;

export type SelectionChangeCallback = (ids: string[]) => Promise<void>;

export interface AttributeData {
  id: string;
  icon: string;
  name: string;
  type: string;
  description: string;
}

export interface AttributeFiltersData {
  name?: string;
  type?: string;
  object?: string;
}
